import React from 'react';
import { Table, Button } from '@crisellevaldez/speculo-ui';
import { useToast } from '@crisellevaldez/speculo-ui';
import { HiHome } from 'react-icons/hi';
import { formatDate } from '../../../../utils/dateTime';
import { formatCurrency } from '../../../../utils/formatters';
import { TablePagination } from '../../../common/TablePagination';
import { TableCard } from '../../../common/TableCard';
import { ActivityTableHeader } from './ActivityTableHeader';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useTenant } from '../../../../hooks/client/tenant/useTenant';
import { useContacts } from '../../../../hooks/useContacts';
import { useContactsData } from '../../../../hooks/useContactsData';
import { useContactValuationsGQL } from '../../../../hooks/graphql/contacts/useContactValuationsGQL';
import type { GQLValuationItem } from '../../../../hooks/graphql/contacts/useContact';
import { isPreviewContactId, PREVIEW_VALUATIONS } from '../../../../pages/previewMockData';

interface HomeValuationsTabProps {
  contactId: string;
  firebaseUid?: string;
  isActive?: boolean;
  onContactUpdate?: () => void | Promise<void>;
}

interface AddressObj { address: string; city: string; state: string; zip: string; }

const getColumns = (websiteDomain: string, onAssignAddress: (address: AddressObj) => void) => [
  {
    header: 'Property',
    key: 'property',
    width: '450px',
    cell: (item: GQLValuationItem) => {
      const fullAddress = item.fullAddress || item.addressStreet || 'Unknown Address';
      const city = item.addressCity || '';
      const state = item.addressState || '';
      const zip = item.addressZip || '';
      const propertyUrl = websiteDomain && item.valuationId
        ? `https://${websiteDomain}/property/${item.valuationId}/valuation`
        : '';
      const addressObj = { address: item.addressStreet || '', city, state, zip };
      return (
        <div className="flex items-start gap-3">
          <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center">
            <HiHome className="h-6 w-6 text-muted-foreground" />
          </div>
          <div className="flex-grow">
            {propertyUrl ? (
              <a href={propertyUrl} target="_blank" rel="noopener noreferrer" className="font-medium underline" title={fullAddress}>{fullAddress}</a>
            ) : (
              <div className="font-medium">{fullAddress}</div>
            )}
            <div className="flex items-center justify-between gap-2">
              <div className="flex-grow">
                <div className="text-sm text-muted-foreground">{city && state && `${city}, ${state} ${zip}`}</div>
                {item.bedrooms != null && (
                  <div className="text-sm text-muted-foreground">
                    {item.bedrooms || 0} bed • {item.bathrooms || 0} bath
                    {item.squareFeet != null && item.squareFeet > 0 && ` • ${item.squareFeet.toLocaleString()} sqft`}
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                <Button variant="secondary" size="sm" onClick={() => onAssignAddress(addressObj)} disabled={!item.addressStreet} className="text-xs whitespace-nowrap">
                  Use Address
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    },
  },
  {
    header: 'Estimated Value',
    key: 'estimatedValue',
    width: '140px',
    cell: (item: GQLValuationItem) => (
      <div className="font-medium">{item.estimatedValue ? formatCurrency(item.estimatedValue) : 'N/A'}</div>
    ),
  },
  {
    header: 'Lot Size',
    key: 'lotSize',
    width: '120px',
    cell: (item: GQLValuationItem) => {
      const lotSize = item.lotSize;
      return <div className="text-sm">{lotSize ? `${lotSize.toLocaleString()} sqft` : 'N/A'}</div>;
    },
  },
  {
    header: 'Created At',
    key: 'createdAt',
    width: '180px',
    cell: (item: GQLValuationItem) => {
      const { organization } = useOrganizationStore.getState();
      return formatDate(item.createdAt ?? '', organization?.timezone);
    },
  },
];

export const HomeValuationsTab: React.FC<HomeValuationsTabProps> = ({ contactId, onContactUpdate }) => {
  const { showToast } = useToast();
  const { refetch } = useContactsData();
  const { editContact } = useContacts(refetch);
  const { organization } = useOrganizationStore();
  const { data: tenant } = useTenant(organization?.id);
  const websiteDomain = tenant?.domain?.primary || '';

  const isPreview = isPreviewContactId(contactId);
  const liveValuations = useContactValuationsGQL(isPreview ? '' : contactId);
  const items: any[] = isPreview ? PREVIEW_VALUATIONS : liveValuations.items;
  const total = isPreview ? PREVIEW_VALUATIONS.length : liveValuations.total;
  const hasNext = isPreview ? false : liveValuations.hasNext;
  const loading = isPreview ? false : liveValuations.loading;
  const handleNextPage = liveValuations.handleNextPage;

  const handleAssignAddress = React.useCallback(
    async (address: AddressObj) => {
      if (!address || !contactId) return;
      await editContact(contactId, {
        address: { street: address.address || '', city: address.city || '', state: address.state || '', zip: address.zip || '' },
      } as Parameters<typeof editContact>[1]);
      showToast({ message: 'Address assigned to contact successfully', variant: 'success' });
      if (onContactUpdate) await onContactUpdate();
    },
    [contactId, editContact, showToast, onContactUpdate]
  );

  const columns = React.useMemo(() => getColumns(websiteDomain, handleAssignAddress), [websiteDomain, handleAssignAddress]);

  return (
    <div className="flex flex-col gap-4">
      <ActivityTableHeader
        title="Home Valuations"
        count={total}
        isLoading={loading}
        showDateRange={false}
      />

      <TableCard
        borderless
        className="flex-shrink-0"
        bodyClassName="min-h-[200px] max-h-[640px] overflow-y-auto scrollbar-custom"
        footer={
          hasNext ? (
            <TablePagination
              hasNext={hasNext}
              onNextPage={handleNextPage}
              isLoading={loading}
            />
          ) : null
        }
      >
        <Table
          data={items}
          columns={columns}
          keyExtractor={(item) => item.valuationId}
          emptyStateText="No home valuations available"
          size="xl"
          loading={loading && items.length === 0}
        />
      </TableCard>
    </div>
  );
};
